import React, { useEffect, useState } from "react";
import { Mail, CreditCard, Send, Check, ShieldCheck, FlaskConical, Globe, Users, BarChart3, CalendarPlus, Ban } from "lucide-react";
import { api, apiError } from "../api";
import { PageLoader, Spinner, StatusBadge, Toast, useToast } from "../components/ui";
import { AdminMasterScans } from "../components/AdminMasterScans";

const SUB_CHIP = {
  active: "bg-emerald-light text-emerald",
  trialing: "bg-teal-light text-teal",
  cancelled: "bg-coral-light text-coral",
  inactive: "bg-amber-light text-amber",
};

function Stat({ label, value, accent = "text-ink", testId }) {
  return (
    <div className="rounded-card border border-stone-border p-3" data-testid={testId}>
      <div className={`font-display text-2xl font-bold ${accent}`}>{value}</div>
      <div className="text-xs text-muted mt-0.5">{label}</div>
    </div>
  );
}

export default function Admin() {
  const { toast, show, close } = useToast();
  const [cfg, setCfg] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");

  // Mailjet form
  const [mjFrom, setMjFrom] = useState("");
  const [mjName, setMjName] = useState("");
  const [mjKey, setMjKey] = useState("");
  const [mjSecret, setMjSecret] = useState("");
  const [testTo, setTestTo] = useState("");

  // PayPal form
  const [ppEnv, setPpEnv] = useState("sandbox");
  const [ppAmount, setPpAmount] = useState("");
  const [ppCurrency, setPpCurrency] = useState("");
  const [ppWebhook, setPpWebhook] = useState("");
  const [ppId, setPpId] = useState("");
  const [ppSecret, setPpSecret] = useState("");

  const hydrate = (data) => {
    setCfg(data);
    setMjFrom(data.mailjet.from_email || "");
    setMjName(data.mailjet.from_name || "");
    setPpEnv(data.paypal.env || "sandbox");
    setPpAmount(data.paypal.amount || "");
    setPpCurrency(data.paypal.currency || "");
    setPpWebhook(data.paypal.app_url ? `${data.paypal.app_url}/api/payments/webhook` : "");
    setMjKey("");
    setMjSecret("");
    setPpId("");
    setPpSecret("");
  };

  useEffect(() => {
    api.get("/admin/config").then((r) => hydrate(r.data)).catch((e) => show(apiError(e), "error"));
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    refreshUsers();
  }, []);

  const refreshUsers = () => api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});

  const extendUser = async (u) => {
    setBusy(`ext-${u.id}`);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/extend`, { days: 30 });
      await refreshUsers();
      show(data.message);
    } catch (e) { show(apiError(e), "error"); } finally { setBusy(""); }
  };

  const cancelUser = async (u) => {
    if (!window.confirm(`Cancel access for ${u.email}? They will lose Pro/trial access.`)) return;
    setBusy(`can-${u.id}`);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/cancel`);
      await refreshUsers();
      show(data.message);
    } catch (e) { show(apiError(e), "error"); } finally { setBusy(""); }
  };

  const saveMailjet = async () => {
    setBusy("mj-save");
    try {
      const body = { from_email: mjFrom, from_name: mjName };
      if (mjKey) body.api_key = mjKey;
      if (mjSecret) body.secret_key = mjSecret;
      const { data } = await api.put("/admin/mailjet", body);
      hydrate(data);
      show("Mailjet settings saved.");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(""); }
  };

  const testMailjet = async () => {
    if (!testTo) { show("Enter a recipient email to test.", "error"); return; }
    setBusy("mj-test");
    try {
      const { data } = await api.post("/admin/mailjet/test", { to_email: testTo });
      show(data.message, data.ok ? "success" : "error");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(""); }
  };

  const savePaypal = async () => {
    setBusy("pp-save");
    try {
      const body = { env: ppEnv, amount: ppAmount, currency: ppCurrency };
      const appUrl = ppWebhook.trim().replace(/\/api\/payments\/webhook\/?$/i, "").replace(/\/+$/, "");
      if (appUrl) body.app_url = appUrl;
      if (ppId) body.client_id = ppId;
      if (ppSecret) body.client_secret = ppSecret;
      const { data } = await api.put("/admin/paypal", body);
      hydrate(data);
      if (data.verify && !data.verify.ok) {
        show(`Saved in ${ppEnv.toUpperCase()} mode, but the credentials failed to authenticate: ${data.verify.message}`, "error");
      } else {
        show(`PayPal saved — now in ${ppEnv.toUpperCase()} mode.`);
      }
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(""); }
  };

  const testPaypal = async () => {
    setBusy("pp-test");
    try {
      const { data } = await api.post("/admin/paypal/test");
      show(data.message, data.ok ? "success" : "error");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(""); }
  };

  if (!cfg) return <PageLoader label="Loading admin" />;

  return (
    <div className="fade-in pb-24">
      <Toast toast={toast} onClose={close} />
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-card bg-teal flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <h1 className="font-display text-2xl md:text-3xl text-ink">Admin</h1>
      </div>

      {/* Metrics */}
      <div className="card p-5 mb-4" data-testid="admin-metrics">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-teal" />
          <h2 className="font-display text-lg text-ink">Overview</h2>
        </div>
        {!metrics ? (
          <p className="text-sm text-muted">Loading metrics…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <Stat label="Users" value={metrics.users.total} testId="metric-users" />
              <Stat label="Active subs" value={metrics.users.active} accent="text-emerald" testId="metric-active" />
              <Stat label="On trial" value={metrics.users.trialing} accent="text-teal" testId="metric-trialing" />
              <Stat label="Inactive" value={metrics.users.inactive + metrics.users.cancelled} accent="text-amber" testId="metric-inactive" />
              <Stat label="Profiles" value={metrics.profiles_total} testId="metric-profiles" />
              <Stat label="Scans (7d)" value={metrics.scans_7d} testId="metric-scans-7d" />
              <Stat label="Scans total" value={metrics.scans_total} testId="metric-scans-total" />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-muted" />
              <h3 className="text-sm font-medium text-ink">Recent scans</h3>
            </div>
            {metrics.recent_scans.length === 0 ? (
              <p className="text-sm text-muted">No scans yet.</p>
            ) : (
              <div className="divide-y divide-stone-border" data-testid="admin-recent-scans">
                {metrics.recent_scans.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`recent-scan-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-ink truncate">{s.profile_name}</div>
                      <div className="text-xs text-muted truncate">{s.user_email} · {s.trigger} · {s.target_date}</div>
                    </div>
                    {s.changes_count > 0 && (
                      <span className="chip bg-coral-light text-coral">{s.changes_count} change{s.changes_count === 1 ? "" : "s"}</span>
                    )}
                    <StatusBadge status={s.status} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Master scans */}
      <AdminMasterScans show={show} />

      {/* User management */}
      <div className="card p-5 mb-4" data-testid="admin-users">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-teal" />
          <h2 className="font-display text-lg text-ink">Users</h2>
          {users && <span className="text-xs text-muted">({users.length})</span>}
        </div>
        {!users ? (
          <p className="text-sm text-muted">Loading users…</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted">No users yet.</p>
        ) : (
          <div className="divide-y divide-stone-border">
            {users.map((u) => (
              <div key={u.id} className="py-3 flex items-center gap-3" data-testid={`user-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-ink truncate">{u.name || u.email}</span>
                    {u.is_admin && <span className="chip bg-teal-light text-teal">admin</span>}
                  </div>
                  <div className="text-xs text-muted truncate">{u.email}</div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`chip ${SUB_CHIP[u.subscription_status] || "bg-stone-border/40 text-muted"}`} data-testid={`user-status-${u.id}`}>
                      {u.subscription_status}
                    </span>
                    {u.subscription_status === "trialing" && (
                      <span className="text-xs text-muted">{u.trial_days_left}d left</span>
                    )}
                    <span className="text-xs text-muted">· {u.profiles} profile{u.profiles === 1 ? "" : "s"}</span>
                  </div>
                </div>
                {!u.is_admin && (
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    <button
                      className="btn-ghost text-xs px-2.5 py-1.5"
                      onClick={() => extendUser(u)}
                      disabled={busy === `ext-${u.id}`}
                      data-testid={`user-extend-${u.id}`}
                    >
                      {busy === `ext-${u.id}` ? <Spinner /> : <CalendarPlus className="w-3.5 h-3.5" />} +30d
                    </button>
                    {u.subscription_status !== "cancelled" && (
                      <button
                        className="btn-ghost text-xs px-2.5 py-1.5 text-coral hover:bg-coral-light"
                        onClick={() => cancelUser(u)}
                        disabled={busy === `can-${u.id}`}
                        data-testid={`user-cancel-${u.id}`}
                      >
                        {busy === `can-${u.id}` ? <Spinner /> : <Ban className="w-3.5 h-3.5" />} Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mailjet */}
      <div className="card p-5 mb-4" data-testid="admin-mailjet">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-teal" />
          <h2 className="font-display text-lg text-ink">Mailjet (email)</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="mjFrom">Sender email</label>
            <input id="mjFrom" className="input" value={mjFrom} onChange={(e) => setMjFrom(e.target.value)}
              placeholder="pfu@yourdomain.com" data-testid="admin-mj-from-email" />
            <p className="text-xs text-muted mt-1">Must be a validated sender in your Mailjet account.</p>
          </div>
          <div>
            <label className="label" htmlFor="mjName">Sender name</label>
            <input id="mjName" className="input" value={mjName} onChange={(e) => setMjName(e.target.value)}
              placeholder="PFU Tracker" data-testid="admin-mj-from-name" />
          </div>
          <div>
            <label className="label" htmlFor="mjKey">API key</label>
            <input id="mjKey" type="password" className="input" value={mjKey} onChange={(e) => setMjKey(e.target.value)}
              placeholder={cfg.mailjet.api_key_set ? `Saved (${cfg.mailjet.api_key_masked}) — leave blank to keep` : "Enter Mailjet API key"}
              data-testid="admin-mj-api-key" />
          </div>
          <div>
            <label className="label" htmlFor="mjSecret">Secret key</label>
            <input id="mjSecret" type="password" className="input" value={mjSecret} onChange={(e) => setMjSecret(e.target.value)}
              placeholder={cfg.mailjet.secret_key_set ? `Saved (${cfg.mailjet.secret_key_masked}) — leave blank to keep` : "Enter Mailjet secret key"}
              data-testid="admin-mj-secret-key" />
          </div>
          <button className="btn-primary w-full" onClick={saveMailjet} disabled={busy === "mj-save"} data-testid="admin-mj-save">
            {busy === "mj-save" ? <Spinner /> : <Check className="w-4 h-4" />} Save Mailjet settings
          </button>

          <div className="pt-3 border-t border-stone-border">
            <label className="label" htmlFor="mjTest">Send a test email to</label>
            <div className="flex gap-2">
              <input id="mjTest" className="input flex-1" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.com" data-testid="admin-mj-test-email" />
              <button className="btn-ghost whitespace-nowrap" onClick={testMailjet} disabled={busy === "mj-test"} data-testid="admin-mj-test-btn">
                {busy === "mj-test" ? <Spinner /> : <Send className="w-4 h-4" />} Send test
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PayPal */}
      <div className="card p-5 mb-4" data-testid="admin-paypal">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-coral" />
          <h2 className="font-display text-lg text-ink">PayPal (payments)</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Environment</label>
            <div className="flex gap-2" data-testid="admin-pp-env">
              {["sandbox", "live"].map((env) => (
                <button
                  key={env}
                  onClick={() => setPpEnv(env)}
                  className={`flex-1 min-h-[44px] rounded-card border text-sm font-medium capitalize transition-colors flex items-center justify-center gap-2 ${
                    ppEnv === env ? (env === "live" ? "bg-coral text-white border-coral" : "bg-teal text-white border-teal") : "border-stone-border text-ink"
                  }`}
                  data-testid={`admin-pp-env-${env}`}
                >
                  {env === "live" ? <Globe className="w-4 h-4" /> : <FlaskConical className="w-4 h-4" />}
                  {env === "sandbox" ? "Sandbox (demo)" : "Live"}
                </button>
              ))}
            </div>
            {ppEnv === "live" && cfg.paypal.env !== "live" && (
              <p className="text-xs text-amber mt-2" data-testid="admin-pp-live-warning">
                Switching to <strong>Live</strong> requires your <strong>Live</strong> PayPal Client ID &amp; Secret below (sandbox keys won't work in live). Enter them, then Save.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="ppAmount">Charge amount</label>
              <input id="ppAmount" className="input" value={ppAmount} onChange={(e) => setPpAmount(e.target.value)}
                placeholder="11.00" data-testid="admin-pp-amount" />
            </div>
            <div>
              <label className="label" htmlFor="ppCurrency">Currency</label>
              <input id="ppCurrency" className="input" value={ppCurrency} onChange={(e) => setPpCurrency(e.target.value.toUpperCase())}
                placeholder="USD" data-testid="admin-pp-currency" />
            </div>
          </div>
          <p className="text-xs text-muted -mt-1">
            Customers are charged and shown <strong>{ppAmount || cfg.paypal.amount} {ppCurrency || cfg.paypal.currency}/month</strong> across the app.
          </p>
          <div>
            <label className="label" htmlFor="ppId">Client ID</label>
            <input id="ppId" type="password" className="input" value={ppId} onChange={(e) => setPpId(e.target.value)}
              placeholder={cfg.paypal.client_id_set ? `Saved (${cfg.paypal.client_id_masked}) — leave blank to keep` : "Enter PayPal Client ID"}
              data-testid="admin-pp-client-id" />
          </div>
          <div>
            <label className="label" htmlFor="ppSecret">Client secret</label>
            <input id="ppSecret" type="password" className="input" value={ppSecret} onChange={(e) => setPpSecret(e.target.value)}
              placeholder={cfg.paypal.client_secret_set ? `Saved (${cfg.paypal.client_secret_masked}) — leave blank to keep` : "Enter PayPal Client secret"}
              data-testid="admin-pp-client-secret" />
          </div>
          <div>
            <label className="label" htmlFor="ppWebhook">Webhook URL</label>
            <input id="ppWebhook" className="input" value={ppWebhook} onChange={(e) => setPpWebhook(e.target.value)}
              placeholder="https://yourdomain.com/api/payments/webhook" data-testid="admin-pp-webhook" />
            <p className="text-xs text-muted mt-1">PayPal sends subscription events here. Saving re-registers it with PayPal. Keep the <span className="text-ink">/api/payments/webhook</span> path.</p>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={savePaypal} disabled={busy === "pp-save"} data-testid="admin-pp-save">
              {busy === "pp-save" ? <Spinner /> : <Check className="w-4 h-4" />} Save PayPal
            </button>
            <button className="btn-ghost flex-1" onClick={testPaypal} disabled={busy === "pp-test"} data-testid="admin-pp-test-btn">
              {busy === "pp-test" ? <Spinner /> : <FlaskConical className="w-4 h-4" />} Test credentials
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
